import { BayLevelEnum } from "open-vessel-definition";
import type { IBayCgsInfo } from "./getContainersCgs";
import type { IBayLevelOffsetBottomBase } from "open-vessel-definition";
import type { IBayPattern } from "../../types/IPositionPatterns";
import { VCG_SEPARATION_TO_DECK } from "../../consts";
import { sortNumericAsc } from "@tedivo/tedivo-pure-helpers";

/** Get the Most observed in between levels vcg */
export function calculateGeneralMedianVcg(
  bayLevelVcgsInfo: Record<IBayPattern, IBayCgsInfo>,
  missingImportantVcgs: boolean,
  offsetBottomBases: Array<IBayLevelOffsetBottomBase>,
): {
  generalMedianVcg: number;
  midVcgByBay: Record<IBayPattern, number>;
  offsetBottomBasesDict: Record<string, number>;
} {
  const midVcgByBay: Record<IBayPattern, number> = {};
  const stats: Map<number, number> = new Map();

  const offsetBottomBasesDict = missingImportantVcgs
    ? offsetBottomBases.reduce((acc, bb) => {
        bb.bays.forEach((b) => {
          acc[`${b}-${bb.level}`] = bb.offset;
        });
        return acc;
      }, {} as Record<string, number>)
    : ({} as Record<string, number>);

  const everyBay = Object.keys(bayLevelVcgsInfo).sort(
    sortNumericAsc,
  ) as IBayPattern[];

  everyBay.forEach((bay) => {
    const bayVcg = bayLevelVcgsInfo[bay];
    const bayVcgsAbove = bayVcg[BayLevelEnum.ABOVE];
    const bayVcgsBelow = bayVcg[BayLevelEnum.BELOW];

    if (bayVcgsAbove === undefined && bayVcgsBelow === undefined) return;

    const adjAboveBB =
      offsetBottomBasesDict[`${bay}-${BayLevelEnum.ABOVE}`] || 0;
    const adjBelowBB =
      offsetBottomBasesDict[`${bay}-${BayLevelEnum.BELOW}`] || 0;

    let midVcgOfThisBay = 0;

    if (bayVcgsAbove === undefined && bayVcgsBelow !== undefined) {
      // If there is no aboveDeck, then we use the belowDeck height
      midVcgOfThisBay =
        bayVcgsBelow.maxHeightWithSeparationAbsoluteOfBay +
        adjBelowBB +
        VCG_SEPARATION_TO_DECK;
    } else if (bayVcgsBelow === undefined && bayVcgsAbove !== undefined) {
      // If there is no belowDeck, then we use the aboveDeck bottomBase
      midVcgOfThisBay =
        bayVcgsAbove.bottomBase + adjAboveBB - VCG_SEPARATION_TO_DECK;
    } else if (bayVcgsAbove !== undefined && bayVcgsBelow !== undefined) {
      const aboveBottomBase = bayVcgsAbove.bottomBase + adjAboveBB;
      const belowMaxHeightWithSeparation =
        adjBelowBB + bayVcgsBelow.maxHeightWithSeparationAbsoluteOfBay;

      midVcgOfThisBay = (aboveBottomBase + belowMaxHeightWithSeparation) / 2;
    }

    midVcgByBay[bay] = midVcgOfThisBay;

    if (midVcgOfThisBay !== Infinity) {
      const statsOfValue = stats.get(midVcgOfThisBay) || 0;
      stats.set(midVcgOfThisBay, statsOfValue + 1);
    }
  });

  const generalMedianVcg =
    Array.from(stats.keys()).sort((a, b) => b - a)[0] || 0;

  return {
    midVcgByBay,
    generalMedianVcg,
    offsetBottomBasesDict,
  };
}
